import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Sparkles, UserCog, ArrowLeft, Calendar, FileText, HelpCircle, Phone, Mail, MapPin, User } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import { MASCOT } from '@/constants/testIds';
import { DubiSvg } from './DubiMascot';

function uid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'sid-' + Math.random().toString(36).slice(2) + Date.now();
}

const WELCOME = "Zdravo! 👋 Ja sam Dubi, vaš pomoćnik za dubinsko čišćenje. Kako mogu da pomognem?";

const QUICK_REPLIES = [
  { id: 'quote', label: 'Cena / ponuda', icon: FileText, prompt: 'Koliko košta dubinsko čišćenje?' },
  { id: 'book',  label: 'Zakaži čišćenje', icon: Calendar, action: 'book' },
  { id: 'services', label: 'O uslugama', icon: HelpCircle, prompt: 'Koje usluge nudite?' },
  { id: 'operator', label: 'Razgovaraj sa operaterom', icon: UserCog, action: 'operator' },
];

function track(name, params = {}) {
  trackEvent(name, params);
}

export default function MascotChat() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('chat'); // 'chat' | 'lead' | 'lead_done'
  const [showTeaser, setShowTeaser] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: WELCOME }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [operatorStatus, setOperatorStatus] = useState({ is_online: false, away_message: '', online_message: '' });

  const [lead, setLead] = useState({ name: '', phone: '', email: '', address: '', message: '' });
  const [leadIntent, setLeadIntent] = useState('operator');
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadError, setLeadError] = useState('');

  const [sessionId] = useState(() => {
    const cached = localStorage.getItem('dubinko_chat_session');
    if (cached) return cached;
    const fresh = uid();
    localStorage.setItem('dubinko_chat_session', fresh);
    return fresh;
  });
  const endRef = useRef(null);

  const isHidden = location.pathname.startsWith('/admin');

  useEffect(() => {
    const t = setTimeout(() => setShowTeaser(true), 2200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    api.get('/operator/status').then(({ data }) => setOperatorStatus(data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, mode]);

  // Allow other parts of the app (e.g. hero mascot) to open the chat
  useEffect(() => {
    const handler = () => openChat('hero-mascot');
    window.addEventListener('dubi:open', handler);
    return () => window.removeEventListener('dubi:open', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openChat = useCallback((source = 'bubble') => {
    setOpen(true);
    setShowTeaser(false);
    track('chat_opened', { source });
  }, []);

  const sendText = async (text) => {
    if (!text || loading) return;
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post('/chat/message', { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: 'Izvinjavam se, trenutno ne mogu da odgovorim. Pokušajte ponovo za nekoliko sekundi.' }]);
    } finally {
      setLoading(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    track('chat_message_sent');
    await sendText(text);
  };

  const handleQuick = (qr) => {
    if (qr.action === 'book') {
      track('quote_requested', { source: 'chat_quick_book' });
      setOpen(false);
      navigate('/rezervacija');
      return;
    }
    if (qr.action === 'operator') {
      track('operator_requested');
      setLeadIntent('operator');
      setMode('lead');
      return;
    }
    if (qr.id === 'quote') track('quote_requested', { source: 'chat_quick' });
    sendText(qr.prompt);
  };

  const submitLead = async (e) => {
    e?.preventDefault?.();
    setLeadBusy(true); setLeadError('');
    try {
      await api.post('/chat/lead', {
        session_id: sessionId,
        name: lead.name,
        phone: lead.phone,
        email: lead.email || null,
        address: lead.address,
        message: lead.message,
        intent: leadIntent,
      });
      track('lead_submitted', { intent: leadIntent });
      setMode('lead_done');
    } catch (e2) {
      setLeadError(formatApiError(e2));
    } finally {
      setLeadBusy(false);
    }
  };

  if (isHidden) return null;

  return (
    <div className="fixed bottom-20 right-5 z-50 flex flex-col items-end gap-3 max-w-[92vw]">
      {!open && showTeaser && (
        <div className="mascot-pop max-w-[260px] glass rounded-2xl px-4 py-3 text-sm text-slate-800 shadow-xl">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
            <p>Zdravo, ja sam Dubi! Treba pomoć oko izbora čišćenja?</p>
          </div>
          <div className="mt-2 text-right">
            <button onClick={() => setShowTeaser(false)} className="text-xs text-slate-500 hover:text-slate-700">Sakrij</button>
          </div>
        </div>
      )}

      {open && (
        <div className="mascot-pop w-[360px] max-w-[92vw] h-[560px] max-h-[88vh] flex flex-col rounded-3xl overflow-hidden shadow-2xl glass border border-white/60">
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between text-white"
               style={{ background: 'linear-gradient(135deg, #023E8A 0%, #5B21B6 100%)' }}>
            <div className="flex items-center gap-3 min-w-0">
              {mode === 'lead' && (
                <button onClick={() => setMode('chat')} className="p-1.5 rounded-full hover:bg-white/15">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <div className="relative shrink-0">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                  <DubiSvg className="w-12 h-12 -mb-1" />
                </div>
                <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${operatorStatus.is_online ? 'bg-emerald-400' : 'bg-amber-400'}`} />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-sm leading-tight">Dubi</div>
                <div className="text-[11px] opacity-90 truncate">
                  {operatorStatus.is_online ? 'Operater online · AI + ljudi' : 'AI asistent · operater offline'}
                </div>
              </div>
            </div>
            <button data-testid={MASCOT.close} onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/15">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          {mode === 'chat' && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white">
                {messages.map((m, i) => (
                  <div key={i} data-testid={MASCOT.message} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-3.5 py-2.5 text-sm rounded-2xl whitespace-pre-wrap leading-relaxed ${
                      m.role === 'user'
                        ? 'bg-slate-900 text-white rounded-br-md'
                        : 'bg-slate-100 text-slate-800 rounded-bl-md'
                    }`}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="px-3.5 py-2.5 bg-slate-100 rounded-2xl text-sm text-slate-500">Kucam…</div>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              <div className="px-3 pt-2 pb-1 flex gap-1.5 overflow-x-auto border-t border-slate-100 bg-white">
                {QUICK_REPLIES.map((qr) => {
                  const I = qr.icon;
                  return (
                    <button
                      key={qr.id}
                      data-testid={`mascot-quick-${qr.id}`}
                      onClick={() => handleQuick(qr)}
                      className="shrink-0 text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1.5">
                      <I className="w-3 h-3" /> {qr.label}
                    </button>
                  );
                })}
              </div>

              <div className="p-3 flex gap-2 bg-white border-t border-slate-100">
                <input
                  data-testid={MASCOT.input}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && send()}
                  placeholder="Napišite poruku…"
                  className="flex-1 px-3.5 py-2.5 rounded-full bg-slate-100 text-sm outline-none focus:ring-2 focus:ring-blue-300"
                />
                <button
                  data-testid={MASCOT.send}
                  onClick={send}
                  disabled={loading || !input.trim()}
                  className="btn-primary px-3 py-2 rounded-full disabled:opacity-50"
                  aria-label="Send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {mode === 'lead' && (
            <form onSubmit={submitLead} className="flex-1 overflow-y-auto p-4 bg-white" data-testid="mascot-lead-form">
              <div className={`mb-3 p-3 rounded-2xl text-sm ${operatorStatus.is_online ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'}`}>
                <strong>{operatorStatus.is_online ? 'Operater je online ✅' : 'Operater nije online'}</strong>
                <div className="mt-1 text-[13px]">
                  {operatorStatus.is_online ? operatorStatus.online_message : operatorStatus.away_message}
                </div>
              </div>

              <div className="space-y-3">
                <FieldIcon icon={User}>
                  <input
                    required minLength={2} data-testid="mascot-lead-name"
                    placeholder="Vaše ime i prezime"
                    value={lead.name} onChange={(e) => setLead({...lead, name: e.target.value})}
                    className="w-full bg-transparent outline-none text-sm" />
                </FieldIcon>
                <FieldIcon icon={Phone}>
                  <input
                    required data-testid="mascot-lead-phone" inputMode="tel"
                    placeholder="Telefon (npr. +381 60 …)"
                    value={lead.phone} onChange={(e) => setLead({...lead, phone: e.target.value})}
                    className="w-full bg-transparent outline-none text-sm" />
                </FieldIcon>
                <FieldIcon icon={Mail}>
                  <input
                    type="email" data-testid="mascot-lead-email"
                    placeholder="Email (opciono)"
                    value={lead.email} onChange={(e) => setLead({...lead, email: e.target.value})}
                    className="w-full bg-transparent outline-none text-sm" />
                </FieldIcon>
                <FieldIcon icon={MapPin}>
                  <input
                    data-testid="mascot-lead-address"
                    placeholder="Adresa (opciono)"
                    value={lead.address} onChange={(e) => setLead({...lead, address: e.target.value})}
                    className="w-full bg-transparent outline-none text-sm" />
                </FieldIcon>
                <textarea
                  data-testid="mascot-lead-message" rows={3}
                  placeholder="Šta vas zanima? (opciono)"
                  value={lead.message} onChange={(e) => setLead({...lead, message: e.target.value})}
                  className="w-full px-3 py-2.5 rounded-2xl border border-slate-300 outline-none focus:border-blue-700 text-sm resize-none" />
                {leadError && <div className="text-xs text-red-700 px-3 py-2 rounded-xl bg-red-50">{leadError}</div>}

                <button
                  type="submit" disabled={leadBusy || !lead.name || !lead.phone}
                  data-testid="mascot-lead-submit"
                  className="w-full btn-primary px-5 py-3 rounded-full font-semibold disabled:opacity-50">
                  {leadBusy ? 'Šaljemo…' : (operatorStatus.is_online ? 'Pošalji — operater će vam pisati' : 'Ostavite kontakt — pozivamo vas')}
                </button>
                <p className="text-[11px] text-slate-500 text-center">
                  Slanjem prihvatate da vas kontaktiramo radi rezervacije ili ponude.
                </p>
              </div>
            </form>
          )}

          {mode === 'lead_done' && (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6 bg-white" data-testid="mascot-lead-success">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <Sparkles className="w-8 h-8" />
              </div>
              <h3 className="font-display text-2xl font-bold mt-4">Hvala, {lead.name.split(' ')[0]}!</h3>
              <p className="text-slate-600 mt-2 text-sm">
                Primili smo vaše podatke. Kontaktiraćemo vas u najkraćem roku na <strong>{lead.phone}</strong>.
              </p>
              <button onClick={() => { setMode('chat'); }}
                className="btn-ghost mt-6 px-5 py-2.5 rounded-full font-semibold">Nazad na razgovor</button>
            </div>
          )}
        </div>
      )}

      {!open && (
        <button
          data-testid={MASCOT.open}
          onClick={() => openChat('bubble')}
          className="relative dubi-float bg-transparent border-0 p-0 focus:outline-none"
          aria-label="Otvori Dubi chat"
        >
          {/* Soft glow behind Dubi (his silhouette shape, not a circle) */}
          <span
            aria-hidden="true"
            className="absolute inset-0 blur-2xl opacity-60 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(124,58,237,0.55) 0%, transparent 65%)' }}
          />
          <DubiSvg className="relative w-48 h-56 drop-shadow-2xl" />
          <span className={`absolute top-6 right-4 w-5 h-5 rounded-full border-2 border-white ${operatorStatus.is_online ? 'bg-emerald-400' : 'bg-amber-400'}`} />
        </button>
      )}
    </div>
  );
}

function FieldIcon({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2.5 rounded-2xl border border-slate-300 focus-within:border-blue-700">
      <Icon className="w-4 h-4 text-slate-500 shrink-0" />
      {children}
    </div>
  );
}
